import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconMail } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { useAuth } from '@/contexts/AuthContext';
import { vnextApiClient } from '@/services/vnext-api';
import { ReportConfigSummary, ReportEscalationResponse } from '@/types';

interface EscalateReportModalProps {
  opened: boolean;
  onClose: () => void;
  /** Report being escalated. ``reportConfigId`` is optional — when absent the
   * modal resolves it from the report itself so it can exclude the current
   * config from the options. */
  report: { id: string; reportConfigId?: string | null };
  /** Called with the escalation link once it succeeds. Escalating to an
   * automated report type yields ``escalated_to_report_id`` (a report that is
   * already running); a manually-fulfilled type yields
   * ``escalated_to_report_request_id`` instead, because the work is scoped and
   * priced in triage before it starts. Callers branch on which is set. */
  onEscalated?: (result: ReportEscalationResponse) => void;
}

export function EscalateReportModal({
  opened,
  onClose,
  report,
  onEscalated,
}: Readonly<EscalateReportModalProps>) {
  const auth = useAuth();
  const isAdmin = !!auth?.user?.isAdministrator;
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [options, setOptions] = useState<ReportConfigSummary[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const selectedConfig = options.find((c) => c.id === selectedConfigId);

  // Resolve the owning team's other report configs whenever the modal opens.
  useEffect(() => {
    if (!opened) {
      return;
    }

    let cancelled = false;
    setLoadingConfigs(true);
    setLoadError(null);
    setSelectedConfigId(null);
    setReason('');
    setOptions([]);

    (async () => {
      try {
        // One fetch: the report yields its current config id (to exclude from
        // the options) and, for admins, its owning team.
        const reportData = await vnextApiClient.getReport(report.id);
        const currentConfigId = report.reportConfigId ?? reportData.report_config_id;

        // Admin escalating a team-less report → no team to scope to.
        if (isAdmin && !reportData.team_id) {
          if (!cancelled) {
            setOptions([]);
          }
          return;
        }

        // Clients MUST omit team_id — the backend scopes configs to the caller's
        // token team (passing team_id as a non-admin 403s). Admins act across
        // teams, so scope to the report's own team.
        const all = await vnextApiClient.getReportConfigs(
          isAdmin
            ? { team_id: reportData.team_id ?? undefined, is_active: true }
            : { is_active: true }
        );
        if (!cancelled) {
          setOptions(all.filter((c) => c.id !== currentConfigId));
        }
      } catch (err) {
        Sentry.captureException(err);
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load report types');
        }
      } finally {
        if (!cancelled) {
          setLoadingConfigs(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [opened, report.id, report.reportConfigId, isAdmin]);

  const handleSubmit = async () => {
    if (!selectedConfigId) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await Sentry.startSpan({ op: 'ui.click', name: 'escalate-report' }, () =>
        vnextApiClient.escalateReport(report.id, {
          report_config_id: selectedConfigId,
          reason: reason.trim() || null,
        })
      );
      // A manually-fulfilled report type has nothing to run yet — it raises a
      // request for triage instead, so don't promise a report is processing.
      if (result.escalated_to_report_request_id) {
        notifications.show({
          title: 'Escalation raised for triage',
          message: isAdmin
            ? 'This report type is fulfilled manually — triage the request to scope, price and staff the work.'
            : 'Our team will confirm the scope of this work before it starts.',
          color: 'blue',
        });
      } else {
        notifications.show({
          title: 'Report escalated',
          message: 'A new report has been created and queued for processing.',
          color: 'green',
        });
      }
      onClose();
      onEscalated?.(result);
    } catch (err) {
      Sentry.captureException(err);
      notifications.show({
        title: 'Escalation failed',
        message: err instanceof Error ? err.message : 'Failed to escalate report',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Escalate report" size="md">
      <Stack>
        <Text size="sm" c="dimmed">
          Re-run this report's subjects under a different report type for deeper investigation. The
          new report builds on the existing subjects.
        </Text>

        {loadError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {loadError}
          </Alert>
        )}

        <Select
          label="Escalate to"
          placeholder={loadingConfigs ? 'Loading report types…' : 'Select a report type'}
          data={options.map((c) => ({ value: c.id, label: c.name }))}
          value={selectedConfigId}
          onChange={setSelectedConfigId}
          disabled={loadingConfigs || options.length === 0}
          rightSection={loadingConfigs ? <Loader size="xs" /> : undefined}
          nothingFoundMessage="No other report types available"
          searchable
          withAsterisk
        />

        {!loadingConfigs && !loadError && options.length === 0 && (
          <Text size="xs" c="dimmed">
            No other report types are available for this team.
          </Text>
        )}

        {selectedConfig?.legacyFlow && (
          <Alert color="blue" icon={<IconAlertCircle size={16} />} data-testid="manual-fulfilment">
            {selectedConfig.name} is researched by our team rather than generated. Escalating raises
            a request for triage — its scope and price are confirmed before any work starts.
          </Alert>
        )}

        <Textarea
          label="Reason (optional)"
          placeholder="Add context for this escalation…"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={5}
        />

        <Divider />

        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconMail size={16} />}
          disabled
          justify="flex-start"
        >
          Contact GTI for bespoke escalations
        </Button>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedConfigId} loading={submitting}>
            Escalate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
